/**
 * Centralized Logger Utility for Á La Cart
 * 
 * Provides consistent, structured logging across all hooks and components.
 * Format: [SCOPE] [ACTION] [STATUS] payload
 * 
 * Levels: DEBUG < INFO < WARN < ERROR
 */

type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

interface LogPayload {
  [key: string]: unknown;
}

// Environment-based log level (could be configurable via env var in the future)
const MIN_LOG_LEVEL: LogLevel = 'DEBUG';

const LOG_LEVEL_ORDER: Record<LogLevel, number> = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
};

const shouldLog = (level: LogLevel): boolean => {
  return LOG_LEVEL_ORDER[level] >= LOG_LEVEL_ORDER[MIN_LOG_LEVEL];
};

const formatTimestamp = (): string => {
  return new Date().toISOString();
};

/**
 * Sanitize payload to remove sensitive data before logging
 */
const sanitizePayload = (payload: unknown): unknown => {
  if (!payload || typeof payload !== 'object') return payload;
  
  const sensitiveKeys = ['password', 'secret', 'token', 'cardNumber', 'cardCvv', 'cvv', 'cpf', 'cnpj'];
  
  if (Array.isArray(payload)) {
    return payload.map(item => sanitizePayload(item));
  }
  
  const sanitized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(payload as Record<string, unknown>)) {
    if (sensitiveKeys.some(sk => key.toLowerCase().includes(sk.toLowerCase()))) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizePayload(value);
    } else {
      sanitized[key] = value;
    }
  }
  return sanitized;
};

const formatLogMessage = (
  level: LogLevel,
  scope: string,
  message: string,
  data?: LogPayload
): string => {
  const timestamp = formatTimestamp();
  const prefix = `[${timestamp}] [${scope}] [${level}]`;
  return data 
    ? `${prefix} ${message} ${JSON.stringify(sanitizePayload(data), null, 2)}`
    : `${prefix} ${message}`;
};

export const logger = {
  debug: (scope: string, message: string, data?: LogPayload): void => {
    if (!shouldLog('DEBUG')) return;
    console.debug(formatLogMessage('DEBUG', scope, message, data));
  },

  info: (scope: string, message: string, data?: LogPayload): void => {
    if (!shouldLog('INFO')) return;
    console.info(formatLogMessage('INFO', scope, message, data));
  },

  warn: (scope: string, message: string, data?: LogPayload): void => {
    if (!shouldLog('WARN')) return;
    console.warn(formatLogMessage('WARN', scope, message, data));
  },

  error: (scope: string, message: string, data?: LogPayload): void => {
    if (!shouldLog('ERROR')) return;
    console.error(formatLogMessage('ERROR', scope, message, data));
  },

  /**
   * Helper methods specifically for mutations
   */
  mutation: {
    start: (scope: string, action: string, payload?: unknown): void => {
      logger.info(scope, `${action} - STARTING`, {
        timestamp: formatTimestamp(),
        payload: sanitizePayload(payload) as LogPayload,
      });
    },

    success: (scope: string, action: string, result?: unknown): void => {
      logger.info(scope, `${action} - SUCCESS`, {
        timestamp: formatTimestamp(),
        result: result as LogPayload,
      });
    },

    error: (scope: string, action: string, error: unknown): void => {
      const errorDetails = error instanceof Error 
        ? { 
            message: error.message, 
            name: error.name,
            stack: error.stack?.split('\n').slice(0, 3).join('\n'),
          }
        : { raw: String(error) };

      logger.error(scope, `${action} - ERROR`, {
        timestamp: formatTimestamp(),
        error: errorDetails,
      });
    },

    validation: (scope: string, action: string, field: string, value?: unknown): void => {
      logger.warn(scope, `${action} - VALIDATION_FAILED`, {
        timestamp: formatTimestamp(),
        field,
        providedValue: value !== undefined ? sanitizePayload(value) : 'undefined',
      });
    },
  },

  /**
   * Helper for database operations
   */
  db: {
    query: (scope: string, table: string, operation: string, filters?: LogPayload): void => {
      logger.debug(scope, `DB_QUERY - ${operation.toUpperCase()} on ${table}`, filters);
    },

    error: (scope: string, error: { code?: string; message: string; details?: string }): void => {
      logger.error(scope, 'DB_ERROR', {
        code: error.code || 'UNKNOWN',
        message: error.message,
        details: error.details,
      });
    },
  },

  /**
   * Helper for validation
   */
  validation: {
    failed: (scope: string, field: string, reason: string): void => {
      logger.warn(scope, 'VALIDATION_FAILED', { field, reason });
    },

    passed: (scope: string, fieldsChecked: string[]): void => {
      logger.debug(scope, 'VALIDATION_PASSED', { fieldsChecked });
    },
  },
};

export default logger;
